import { CSSProperties } from 'react';
import { SharedPreviewComponent } from 'configurator-ui-shared';

import { useRootSelector } from '../../../store';
import { useGetProductQuery } from '../../api';
import { SHOPIFY_PRODUCT_TAG } from '../../../config';
import { selectAvailableUpsellItems } from '../../upsells/store/upsellsSlice';
import {
  selectAllConfigSnapshots,
  selectMultiConfigEnabled
} from '../store/multiConfigSlice';

interface HiddenConfigPreviewsProps {
  productTag?: string;
}

const hiddenContainerStyle: CSSProperties = {
  position: 'absolute',
  width: 0,
  height: 0,
  overflow: 'hidden',
  opacity: 0,
  pointerEvents: 'none'
};

// Renders one off-screen preview per configuration in the session - for the
// base product, and for each currently-available upsell - each driven by
// that configuration's own (not necessarily active) properties via
// itemPropertiesOverride/domIdOverride. This is what lets add-to-cart
// (useSendMultiConfigSessionToCart/useAddMultiConfigUpsellToCart) capture
// every configuration's rendered preview without switching the
// visibly-active configuration first, which was the only way to get each
// configuration's snapshot rendered before this existed - but did so on
// the exact same node the shopper is looking at, causing every preview on
// screen to visibly flash through each configuration in turn.
export function HiddenConfigPreviews({
  productTag
}: HiddenConfigPreviewsProps) {
  const multiConfigEnabled = useRootSelector(selectMultiConfigEnabled);
  const configSnapshots = useRootSelector(selectAllConfigSnapshots);
  const availableUpsellItems = useRootSelector(selectAvailableUpsellItems);
  const { data: apiProduct } = useGetProductQuery(
    productTag || SHOPIFY_PRODUCT_TAG
  );

  if (!multiConfigEnabled) return null;

  return (
    <div aria-hidden="true" style={hiddenContainerStyle}>
      {configSnapshots.map(snapshot => (
        <SharedPreviewComponent
          key={`base-${snapshot.id}`}
          product={apiProduct}
          itemPropertiesOverride={snapshot.properties}
          domIdOverride={`config-preview-${snapshot.id}`}
        />
      ))}
      {configSnapshots.flatMap(snapshot =>
        availableUpsellItems.map(upsell => (
          <SharedPreviewComponent
            key={`upsell-${upsell.product_id}-${snapshot.id}`}
            product={upsell}
            isUpsell
            itemPropertiesOverride={snapshot.properties}
            domIdOverride={`config-preview-${snapshot.id}-upsell-${upsell.product_id}`}
          />
        ))
      )}
    </div>
  );
}
